import { useIntl } from 'react-intl';
import * as z from 'zod';

import { MappedForm } from '@/components/elements/form-map/form-map.component';
import { CreateProductGroupInput, CreateProductGroupMutation, UpdateProductGroupInput } from '@/gql/graphql';
import { useToast } from '@/hooks/use-toast.hook';
import { useDialogStore } from '@/store/dialog.store';
import { FetchResult, useMutation } from '@apollo/client';
import { allProductGroups, createProductGroup, updateProductGroup } from '../../product-group.requests';
import { productGroupsToastTranslations } from '../../product-groups.messages';
import { productMap } from './product-group-form.config';
import { productFormSchema } from './product-group-form.validation';

export type GroupProductFormInitialValues = {
  id: string;
  name: string;
  description: string;
};

type ProductFormProps = {
  edit?: boolean;
  initialValues?: Partial<GroupProductFormInitialValues>;
  handleSubmit?: (values: CreateProductGroupInput | UpdateProductGroupInput) => void;
  afterAdd?: (result: FetchResult<CreateProductGroupMutation>) => void;
};

export const ProductGroupForm = ({ initialValues, edit, afterAdd }: ProductFormProps) => {
  const intl = useIntl();
  const { toast } = useToast();

  const { handleDialogClose } = useDialogStore((state) => ({
    handleDialogClose: state.closeDialog,
  }));

  const [createProductGroupMutation, { loading: addProductLoading }] = useMutation(createProductGroup);

  const [editProductGroupMutation, { loading: editProductLoading }] = useMutation(updateProductGroup);

  async function handleSubmit(values: z.infer<ReturnType<typeof productFormSchema>>) {
    if (edit) {
      if (!initialValues) return;
      await editProductGroupMutation({
        variables: {
          updatedProductGroup: {
            id: initialValues.id || '',
            name: values.name,
            description: values.description,
          },
        },
        refetchQueries: [{ query: allProductGroups }],
      });
      handleDialogClose();
      toast({
        description: intl.formatMessage(productGroupsToastTranslations.editProductToast),
        variant: 'default',
      });
      return;
    }

    const data = await createProductGroupMutation({
      variables: {
        createProductGroupInput: {
          name: values.name,
          description: values.description,
        },
      },
      refetchQueries: [{ query: allProductGroups }],
    });
    handleDialogClose();
    toast({
      description: intl.formatMessage(productGroupsToastTranslations.addProductToast),
      variant: 'default',
    });
    if (afterAdd) afterAdd(data);
  }

  return (
    <MappedForm
      context="product"
      loading={addProductLoading || editProductLoading}
      formMap={productMap(intl)}
      defaultValues={{
        name: '',
        description: '',
      }}
      initialValues={initialValues}
      onClose={handleDialogClose}
      onSubmit={handleSubmit}
      validationSchema={productFormSchema}
    />
  );
};
